import type { SetStateAction } from 'react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox } from '@xing-com/checkbox';
import {
  FormMessage,
  PasswordInput,
  ContinueWithApple,
  ContinueWithGoogle,
  Divider,
  FormHeadline,
} from '@xing-com/crate-login-layout';
import { useLoginTracking } from '@xing-com/crate-login-tracking';
import type { LoginFormProps } from '@xing-com/crate-login-types';
import { useSignupLink } from '@xing-com/crate-login-utils';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import { FormField } from '@xing-com/text-field';

import {
  useCreateLogin,
  useCreateOnlyfySignup,
  useIsLoginDomain,
} from './hooks';
import { Styled } from './login-form.styled';
import { messages } from './messages';
import { PartnerNotice } from './partner-notice';
import { getWindowLoginOptions } from './utils';

type LoginFormValues = {
  username: string;
  password: string;
  perm: boolean;
};

export const INITIAL_FORM_VALUES: LoginFormValues = {
  username: '',
  password: '',
  perm: true,
};

const getInitialFormValues = (
  form: React.MutableRefObject<HTMLFormElement | null>
): SetStateAction<LoginFormValues> => {
  const elements = form?.current?.elements;

  if (!elements) return INITIAL_FORM_VALUES;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const usernameField = elements.namedItem('username') as HTMLInputElement;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const passwordField = elements.namedItem('password') as HTMLInputElement;

  return {
    ...INITIAL_FORM_VALUES,
    username: usernameField?.value || '',
    password: passwordField?.value || '',
  };
};

export const LoginForm: React.FC<LoginFormProps> = ({
  authPartner = null,
  loggedOutSid = null,
  noHeadline = false,
  headlineMessage,
  noSignupLink = false,
  loginParams = null,
  initialErrorKey = null,
  onError = () => null,
  showAppleSignIn = true,
  showGoogleSignIn = true,
  usernamePlaceholderMessage = null,
  passwordPlaceholderMessage = null,
  showTroubleLoggingInLink = true,
  onlyfyToken = null,
  signupChannel,
}) => {
  const intl = useIntl();
  const { trackPageView } = useLoginTracking();

  const signupLink = useSignupLink();

  const {
    execute: createLogin,
    error: createLoginError,
    loading: createLoginLoading,
    validationErrors: createLoginValidationErrors,
  } = useCreateLogin();

  const {
    execute: createOnlyfySignup,
    error: createOnlyfySignupError,
    loading: createOnlyfySignupLoading,
  } = useCreateOnlyfySignup();

  const [values, setValues] = useState(INITIAL_FORM_VALUES);
  const [initialErrorMessage, setInitialErrorMessage] = useState(
    initialErrorKey
      ? Object.values(messages).find(({ id }) => id === initialErrorKey) ||
          messages.submitRequestError
      : null
  );
  const formRef = useRef<HTMLFormElement | null>(null);

  const isLoginDomain = useIsLoginDomain();
  const externalLoginUrl = useLoginAppUrl();

  useEffect(() => {
    // this re-initializes the form with browser-prefilled values
    // despite javascript execution taking too long to register the
    // change event triggered by the browser-prefill

    if (formRef) setValues(getInitialFormValues(formRef));
  }, []);

  const errors: Record<string, unknown> = useMemo(() => {
    if (initialErrorMessage) {
      return { general: initialErrorMessage };
    }

    if (createLoginError) {
      return { general: createLoginError };
    }

    if (createOnlyfySignupError) {
      return { general: messages.submitRequestError };
    }

    if (Object.keys(createLoginValidationErrors).length > 0) {
      return createLoginValidationErrors;
    }

    return {};
  }, [
    createLoginError,
    createLoginValidationErrors,
    createOnlyfySignupError,
    initialErrorMessage,
  ]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      onError(errors, createLoginError || createOnlyfySignupError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...[Object.keys(errors)], onError]);

  // can be removed once we migrate everything to crate (not only the login flow
  // but also the pages that are using the accounts package)
  const recoveryLinkProps = isLoginDomain
    ? { to: '/recovery' }
    : { href: `${externalLoginUrl}recovery` };

  const maybeError = (property: string): string => {
    if (!errors[property]) return '';

    const error = errors[property];

    return typeof error === 'string' ? error : intl.formatMessage(error);
  };

  const handleSubmit = async (values: LoginFormValues): Promise<void> => {
    const loginOptions = getWindowLoginOptions(loginParams);

    const payload = {
      ...loginOptions,
      username: values.username,
      password: values.password,
      perm: values.perm ? '1' : '0',
    };

    setInitialErrorMessage(null);
    await createLogin(payload);
  };

  const handleOnlyfyRegister = async (): Promise<void> => {
    // User pressed the registration button in Onlyfy mode.
    // Automatically create their account using the Onlyfy endpoint

    const onlyfyPayload = {
      state: onlyfyToken || '',
      logged_out_sid: loggedOutSid || '',
    };

    setInitialErrorMessage(null);
    await createOnlyfySignup(onlyfyPayload);
  };

  const usernameLabel =
    usernamePlaceholderMessage || intl.formatMessage(messages.usernameLabel);
  const passwordLabel =
    passwordPlaceholderMessage || intl.formatMessage(messages.passwordLabel);

  return (
    <>
      {onlyfyToken ? (
        <Helmet>
          <link rel="canonical" href={externalLoginUrl + 'onlyfy/connect'} />
        </Helmet>
      ) : (
        <Helmet>
          <link rel="canonical" href={externalLoginUrl} />
        </Helmet>
      )}
      <Styled.Container>
        {!noHeadline ? (
          <FormHeadline
            headlineMessage={
              headlineMessage ? (
                <FormattedMessage {...headlineMessage} />
              ) : (
                <FormattedMessage {...messages.loginHeadline} />
              )
            }
            subheadlineMessage={
              noSignupLink ? null : (
                <FormattedMessage
                  {...messages.loginSubheadline}
                  values={{
                    signup: (
                      <Styled.Hyperlink
                        href={onlyfyToken ? '#' : signupLink}
                        // @ts-expect-error Hyperlink does not declare the onClick but can pass it on
                        onClick={onlyfyToken ? handleOnlyfyRegister : undefined}
                        disabled={createOnlyfySignupLoading}
                      >
                        <FormattedMessage {...messages.signupHeadline} />
                      </Styled.Hyperlink>
                    ),
                  }}
                />
              )
            }
          />
        ) : null}

        {authPartner ? <PartnerNotice partner={authPartner} /> : null}

        <form
          data-qa="login-form"
          ref={formRef}
          method={'POST'}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(values);
          }}
        >
          <Styled.InputWrapper>
            <FormField
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              autoCapitalize={'none'}
              autoComplete={'email'}
              size="medium"
              type="email"
              error={Boolean(maybeError('username'))}
              helperText={maybeError('username')}
              name="username"
              id="username"
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, username: e.target.value })
              }
              label={usernameLabel}
              value={values.username}
              data-qa="username"
            />
          </Styled.InputWrapper>

          <Styled.InputWrapper>
            <PasswordInput
              autoComplete="current-password"
              name="password"
              id="password"
              required
              onChange={(e) =>
                setValues({ ...values, password: e.target.value })
              }
              error={Boolean(maybeError('password'))}
              helperText={maybeError('password')}
              label={passwordLabel}
              value={values.password}
              data-qa="password"
            />
          </Styled.InputWrapper>

          <Styled.CheckBoxContainer>
            <Checkbox
              id="perm"
              name="perm"
              // @ts-expect-error FIXME: SC6
              onChange={(e) => setValues({ ...values, perm: e.target.checked })}
              // @ts-expect-error FIXME: SC6
              checked={values.perm}
              data-qa="perm"
            />
            <Styled.Label htmlFor="perm">
              {intl.formatMessage(messages.rememberMe)}
            </Styled.Label>
          </Styled.CheckBoxContainer>

          {errors.general ? (
            <FormMessage error={maybeError('general')} />
          ) : null}

          <Styled.SubmitButton
            variant="primary"
            size="medium"
            loading={createLoginLoading}
            onClick={() => handleSubmit(values)}
            type="submit"
          >
            {intl.formatMessage(messages.submitLabel)}
          </Styled.SubmitButton>

          {showTroubleLoggingInLink ? (
            <Styled.TroubleLoggingInLink
              size="small"
              sizeConfined="small"
              fontWeight="regular"
              {...recoveryLinkProps}
              data-qa="login-problems-link"
            >
              {intl.formatMessage(messages.problemsLoggingIn)}
            </Styled.TroubleLoggingInLink>
          ) : null}

          {showGoogleSignIn || showAppleSignIn ? (
            <Divider>
              <FormattedMessage {...messages.separator} />
            </Divider>
          ) : null}

          {showGoogleSignIn ? (
            <ContinueWithGoogle
              signupChannel={
                signupChannel ? `${signupChannel}_oauth_google` : undefined
              }
              caller="login_page"
              onClick={() => {
                trackPageView({
                  page: 'login/google',
                  PropTrackAction: 'lp_google_click',
                });
              }}
            />
          ) : null}

          {showAppleSignIn ? (
            <ContinueWithApple
              signupChannel={
                signupChannel ? `${signupChannel}_oauth_apple` : undefined
              }
              caller="login_page"
              onClick={() => {
                trackPageView({
                  page: 'login/apple',
                  PropTrackAction: 'lp_apple_click',
                });
              }}
            />
          ) : null}

          <Styled.TandC size="xsmall" sizeConfined="xsmall">
            <FormattedMessage
              {...messages.tandcDescription}
              values={{
                tandc: (
                  <a
                    href="https://www.xing.com/terms"
                    target="'_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage {...messages.tandcLabel} />
                  </a>
                ),
                dataprotection: (
                  <a
                    href={intl.formatMessage(messages.dataprotectionLink)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage {...messages.dataprotectionLabel} />
                  </a>
                ),
              }}
            />
          </Styled.TandC>
        </form>
      </Styled.Container>
    </>
  );
};
